<script setup>
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";

import logoSmall from "@/assets/images/logo_small.svg";
import logoSmallWhite from "@/assets/images/logo_small_white.svg";
import SnsLogo from "@/components/ui/SnsLogo.vue";
import SnsPopper from "@/components/ui/SnsPopper.vue";
import { useTheme } from "@/composables/useTheme";

const props = defineProps({
  collapsed: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["toggle-collapse", "close-mobile"]);

const store = useStore();
const { isDarkTheme, isCustomTheme, hasOnPremLogo } = useTheme();

const softwareVersion = computed(() => store.getters.softwareVersion);
const useSmallLogo = computed(() => !isCustomTheme.value && !hasOnPremLogo.value);
const smallLogoSrc = computed(() => (isDarkTheme.value ? logoSmallWhite : logoSmall));

const isLogoHovered = ref(false);

watch(
  () => props.collapsed,
  () => {
    isLogoHovered.value = false;
  },
);
</script>

<template>
  <div class="sidebar-header">
    <template v-if="!collapsed">
      <div class="sidebar-header-expanded">
        <div class="sidebar-header-left">
          <SnsLogo image-class="h-7 w-auto" />
          <span class="sidebar-header-version">{{ softwareVersion }}</span>
        </div>

        <div class="sidebar-header-toggle-wrapper">
          <sns-popper placement="bottom">
            <template #element>
              <button
                class="sidebar-header-toggle"
                :aria-label="$t('component.sidebar.closeSidebar')"
                @click.stop="emit('toggle-collapse')"
              >
                <i class="bi bi-layout-sidebar" />
              </button>
            </template>
            <template #content>
              {{ $t("component.sidebar.closeSidebar") }}
            </template>
          </sns-popper>
        </div>
      </div>
    </template>

    <template v-else>
      <sns-popper placement="right">
        <template #element>
          <button
            class="sidebar-header-collapsed"
            :aria-label="$t('component.sidebar.openSidebar')"
            @mouseenter="isLogoHovered = true"
            @mouseleave="isLogoHovered = false"
            @click="emit('toggle-collapse')"
          >
            <Transition name="logo-swap" mode="out-in">
              <i v-if="isLogoHovered" key="arrow" class="bi bi-layout-sidebar sidebar-header-expand-icon" />
              <img
                v-else-if="useSmallLogo"
                key="small-logo"
                :src="smallLogoSrc"
                class="sidebar-header-logo-collapsed"
                alt="sensity-ai logo"
              />
              <SnsLogo v-else key="custom-logo" :disable-link="true" image-class="sidebar-header-logo-collapsed" />
            </Transition>
          </button>
        </template>
        <template #content>
          {{ $t("component.sidebar.openSidebar") }}
        </template>
      </sns-popper>
    </template>

    <button class="sidebar-header-close" @click="emit('close-mobile')">
      <i class="bi bi-x close-icon" />
    </button>
  </div>
</template>

<style lang="scss" scoped>
.sidebar-header {
  @apply relative shrink-0;
}

.sidebar-header-expanded {
  @apply flex items-center py-6 pl-5 pr-4 w-full;
}

.sidebar-header-left {
  @apply flex items-center gap-2;
}

.sidebar-header-version {
  @apply text-xs whitespace-nowrap;
  color: oklch(var(--bc) / 0.5);
}

.sidebar-header-toggle-wrapper {
  @apply ml-auto shrink-0 mr-2;

  @media screen and (max-width: $screen-size-tablet) {
    display: none;
  }
}

.sidebar-header-toggle {
  @apply flex items-center justify-center border-none bg-transparent rounded-md p-1.5;
  color: oklch(var(--bc) / 0.5);
  cursor: w-resize;
  font-size: 1.1rem;
}

.sidebar-header-collapsed {
  @apply flex flex-col justify-center items-center gap-2 w-full border-none bg-transparent;
  cursor: e-resize;
  padding: 1.25rem 0.5rem;
  color: oklch(var(--bc));

  :deep(.sns-logo) {
    @apply flex justify-center;
  }
}

.sidebar-header-logo-collapsed {
  @apply h-8 w-auto max-w-full;
}

.sidebar-header-expand-icon {
  @apply text-sm h-8 w-8 flex items-center justify-center rounded-full transition-colors;
  background-color: oklch(var(--n) / 0.1);
}

.logo-swap-enter-active,
.logo-swap-leave-active {
  transition: opacity 0.15s ease;
}

.logo-swap-enter-from,
.logo-swap-leave-to {
  opacity: 0;
}

.sidebar-header-close {
  @apply absolute right-2 top-2 p-0.5 text-[0] bg-transparent outline-none border-none;
  color: oklch(var(--bc));

  @media screen and (min-width: $screen-size-tablet) {
    @apply hidden;
  }

  .close-icon {
    @apply text-[30px];
    color: oklch(var(--bc));
  }
}
</style>
